// Stage 97: WARP — chat-view mount surface (Confusion Monitor).
//
// Sibling to LargeDotWidget in AuthedApp. Subscribes to useGadflyTurn
// (the per-turn pubsub already fed by useChat's SSE handlers) and
// renders the BiteWidget into the chat layout's right sidebar gutter.
//
// The widget owns NO data — it is a pure presentation of useGadflyTurn.
// All animation state lives inside WarpScene; the parent's only job is
// gating mount + threading config props.

import { useGadflyTurn } from '../../hooks/useGadflyTurn';
import { useGadflyTurnAdapter } from './useGadflyTurnAdapter';
import { BiteWidget } from './BiteWidget';
import { IconSprite } from './IconSprite';
import type { WarpThemeChannel } from './types';
import './warp.css';

interface WarpWidgetProps {
  /** Master mount gate. Render null when false. */
  enabled: boolean;
  /** One of the 8 ambient channels. Kind colors are reserved separately. */
  themeChannel: WarpThemeChannel;
  /** Narrow form (360 px panel vs 420 px). */
  compact: boolean;
  /**
   * True when LargeDotWidget is also mounted (partner mode + DOT Large
   * enabled). The widget uses this only to label its subtitle and
   * tighten its top margin so the two widgets sit visually together.
   */
  partneredWithDot: boolean;
}

export function WarpWidget(props: WarpWidgetProps): JSX.Element | null {
  const { enabled, themeChannel, compact, partneredWithDot } = props;
  const hostTurn = useGadflyTurn();
  const turn = useGadflyTurnAdapter(hostTurn);

  if (!enabled) return null;

  const subtitle = partneredWithDot
    ? 'Gadfly Bite stream · partner mode'
    : 'Gadfly Bite stream';

  return (
    <aside
      className={`warp-root channel-${themeChannel}${
        partneredWithDot ? ' is-partnered' : ''
      }`}
      aria-hidden="true"
      aria-label="Gadfly Confusion Monitor"
    >
      <IconSprite />
      <BiteWidget
        turnState={turn}
        compact={compact}
        initialEmptyState={turn.turnId ? null : 'disabled'}
        subtitle={subtitle}
      />
    </aside>
  );
}
